import {
  Divider,
  Image as ChakraImage,
  Stack,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react';
import SectionTitle from '@components/section-title';
import Content from '@components/content';
import Header from '@components/header';
import MainContainer from '@containers/main';
import FadeHeader from '@containers/main/components/fade-header';
import HambergerMenu from '@containers/main/components/hamberger-menu';
import HeaderCart from '@containers/main/header-cart';
import RecentStore from '@containers/main/recent-store';
import UnauthedHeaderAddress from '@containers/main/unauthed/header-address';
import { addressSelector } from '@stores/address';
import { useSession } from 'next-auth/react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch } from 'react-icons/fa';
import { RiDownload2Fill } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import 'swiper/css';
import 'swiper/css/effect-creative';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Link from 'next/link';
import withConfig from '@libs/auth/with-config';
import { ConfigProvider } from 'context/core-config';
import DiscountConstantDisplay from '@containers/main/components/discount-constant-display';
import { TConfigPageProps } from '@@types/base';
import apis from '@apis/index';
import usePopup from '@hooks/usePopup';
import { TPopupProps } from '@@types/popup';
import { set } from 'nprogress';
import { IoIosAppstore } from 'react-icons/io';
import { FaAppStoreIos, FaGooglePlay } from 'react-icons/fa6';

const RecommendedStore = dynamic(
  () => import('@containers/main/recommended-store'),
  { ssr: false },
);

export default function MainPage({ config }: TConfigPageProps) {
  const { t, i18n } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const { status } = useSession();
  const { currentAddress } = useSelector(addressSelector);
  const router = useRouter();
  const { openAlert, openPopup } = usePopup();

  const handleBeforeInstallPrompt = (event: Event) => {
    event.preventDefault();

    setDeferredPrompt(event);
  };

  const handleLineServerNoticeOpen = () => {
  openPopup({
    type: 'modal',
    isOpen: true,
    props: {},
    Component: ({ closePopup }: TPopupProps) => (
      <ModalContent>
        <div className="relative rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 p-6 shadow-lg">
          {/* Close Button */}
          <button
            type="button"
            onClick={closePopup}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <span className="text-xl leading-none text-gray-600">×</span>
          </button>

          {/* Notice Header */}
          <div className="mb-5 pr-8">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-500 shadow-md">
                <span className="text-2xl">⚠️</span>
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-500">
                  ประกาศสำคัญ
                </p>

                <h3 className="text-lg font-bold leading-snug text-gray-900">
                  ขณะนี้ระบบ LINE ขัดข้องชั่วคราว
                </h3>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-gray-700">
              ขณะนี้เซิร์ฟเวอร์ของ LINE ไม่เสถียร
              ทำให้ไม่สามารถรับและส่งข้อความผ่านช่องทาง LINE
              ได้ตามปกติค่ะ
            </p>
          </div>

          {/* Main Guidance */}
          <div className="mb-5 rounded-xl border border-orange-200 bg-white p-5 shadow-sm">
            <p className="mb-3 text-base font-bold text-gray-900">
              กรุณาใช้แอป FFLIK FOOD แทนชั่วคราว
            </p>

            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              ลูกค้าสามารถสั่งอาหาร ตรวจสอบสถานะคำสั่งซื้อ
              และติดต่อฝ่ายบริการลูกค้าผ่านแอป FFLIK FOOD
              ได้ตามปกติค่ะ
            </p>

            <div className="rounded-lg border-l-4 border-orange-400 bg-orange-50 p-3">
              <p className="text-sm leading-relaxed text-gray-700">
                ขออภัยในความไม่สะดวกที่เกิดขึ้น
                และขอขอบคุณสำหรับความเข้าใจค่ะ 🙏
              </p>
            </div>
          </div>

          {/* App Store Links */}
          <div className="space-y-3">
            <a
              href="https://apps.apple.com/app/fflik-food/id6748334036"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-black">
                <FaAppStoreIos className="text-white" size={30} />
              </div>

              <div className="flex-1">
                <p className="text-xs font-medium text-gray-500">iOS</p>
                <p className="font-semibold text-gray-900">
                  ดาวน์โหลดบน App Store
                </p>
              </div>

              <span className="text-gray-400">›</span>
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.u3o.fflik"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-green-400 to-blue-500">
                <FaGooglePlay className="text-white" size={22} />
              </div>

              <div className="flex-1">
                <p className="text-xs font-medium text-gray-500">Android</p>
                <p className="font-semibold text-gray-900">
                  ดาวน์โหลดบน Google Play
                </p>
              </div>

              <span className="text-gray-400">›</span>
            </a>
          </div>

          <Divider className="my-5" />

          {/* Close Button */}
          <button
            type="button"
            onClick={closePopup}
            className="w-full rounded-lg bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 font-semibold text-white shadow-md transition-all hover:from-orange-600 hover:to-red-600"
          >
            ปิดประกาศ
          </button>
        </div>
      </ModalContent>
    ),
  });
};

  const handleAppAdvertisementClick = () => {
    const appAdvertisementClosed = localStorage.getItem(
      'appAdvertisementClosed',
    );
    if (appAdvertisementClosed === 'true') return;
    openPopup({
      type: 'modal',
      isOpen: true,
      props: {},
      Component: ({ closePopup }: TPopupProps) => (
        <ModalContent>
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 shadow-lg border border-orange-100 relative">
            {/* Close Button */}
            <button
              onClick={() => {
                closePopup();
              }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-gray-100 transition-colors shadow-sm border border-gray-200"
              aria-label="Close"
            >
              <span className="text-gray-600 text-xl leading-none">x</span>
            </button>

            {/* App Store Links */}
            <div className="space-y-3 mb-4">
              <a
                href="https://apps.apple.com/app/fflik-food/id6748334036"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-black rounded-xl flex items-center justify-center">
                  <span className="text-black text-xl">
                    <FaAppStoreIos size="42" />
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">iOS</p>
                  <p className="text-gray-900 font-semibold">
                    ดาวน์โหลดบน App Store
                  </p>
                </div>
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.u3o.fflik"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-xl flex items-center justify-center">
                  <span className="text-white text-xl">
                    <FaGooglePlay />
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium">Android</p>
                  <p className="text-gray-900 font-semibold">
                    ดาวน์โหลดบน Google Play
                  </p>
                </div>
              </a>
            </div>

            <Divider className="my-4" />

            {/* Announcement Text */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-orange-100">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-2xl">🍔</span>
                </div>
                <div className="flex-1">
                  {/* <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                    ประกาศเปลี่ยนแบรนด์
                    <span className="text-base">✨</span>
                  </h3> */}
                  <p className="text-gray-700 leading-relaxed mb-3">
                    <span className="font-semibold text-orange-600">
                      Food Panther [ฟู้ดแพนเธอร์]
                    </span>
                    รีแบรนด์เป็น
                    <span className="font-semibold text-orange-600">
                      {' '}
                      FFLIK [ฟลิค APP]
                    </span>
                    เรียบร้อยแล้วนะคะ 🎉
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    ตอนนี้สามารถสั่งอาหารผ่านแอปได้สะดวก รวดเร็ว
                    และปลอดภัยยิ่งขึ้นค่ะ 🍔📱
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    ตอนนี้แอป FFLIK FOOD กำลังจัดกิจกรรมคูปองรีเลย์อยู่นะคะ! 🎉
                  </p>
                  {/* <div className="bg-orange-50 rounded-lg p-3 border-l-4 border-orange-400">
                    <p className="text-sm text-gray-700">
                      💬 หากมีปัญหาหรือข้อไม่สะดวกระหว่างการใช้งาน
                      สามารถทักแชตเข้ามาแจ้งเราได้ทุกเมื่อเลยนะคะ 😊
                    </p>
                  </div> */}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => {
                  localStorage.setItem('appAdvertisementClosed', 'true');
                  closePopup();
                }}
                className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                ไม่ต้องแสดงอีก
              </button>
              <button
                onClick={() => {
                  closePopup();
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors shadow-md"
              >
                ปิด
              </button>
            </div>
          </div>
        </ModalContent>
      ),
    });
  };

  const handleInstall = () => {
    if (deferredPrompt) {
      (deferredPrompt as any).prompt();

      (deferredPrompt as any).userChoice.then(() => {
        setDeferredPrompt(null);
      });
    }
  };

  useEffect(() => {
    if (!window) return;
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  useEffect(() => {
    handleAppAdvertisementClick();
    // handleLineServerNoticeOpen();
  }, []);

  // useEffect(() => {
  //   openAlert({
  //     description: (
  //       <div className="text-center">
  //         <p>
  //           ขณะนี้เกิดข้อผิดพลาดของเซิร์ฟเวอร์ Line
  //           ทำให้การติดต่อกับลูกค้าไม่สะดวกค่ะ หากมีข้อสงสัย สามารถติดต่อเราผ่าน
  //           Facebook ได้ที่ลิงก์ด้านล่างนะคะ ขออภัยในความไม่สะดวกค่ะ 🙏
  //         </p>
  //         <p>สามารถสั่งอาหารได้ตามปกติค่ะ 😊</p>
  //         <Divider />
  //         <a
  //           href="https://www.facebook.com/foodpantherasia"
  //           className="font-bold underline text-zinc-400"
  //         >
  //           ลิงก์ไปยังหน้า Facebook ค่ะ ⬇️
  //         </a>
  //       </div>
  //     ),
  //     isConfirmButtonShow: false,
  //     cancelButtonText: '' + t('common:close'),
  //   });
  // }, []);

  return (
    <>
      <Head>
        <title>FoodPanther</title>
      </Head>
      <Header isPadding={false} className="border-none">
        <div className="w-full bg-primary-500">
          <Content>
            <div className="text-start flex items-center justify-between gap-[12px] w-full h-[60px]">
              <div className="flex items-center gap-[12px]">
                <HambergerMenu />
                <UnauthedHeaderAddress />
              </div>
              <HeaderCart />
            </div>
          </Content>
        </div>
      </Header>
      <ConfigProvider config={config}>
        <FadeHeader className="h-[60px] bg-primary-500 box-content border-b-[1px] border-solid border-secondary-400 text-secondary-400">
          <Content className="flex h-full items-center">
            <button
              className="bg-white w-full py-[8px] px-[12px] rounded-2xl flex items-center gap-[6px] active:scale-95 transition-all duration-100 ease-out"
              onClick={() =>
                router.push(
                  {
                    pathname: '/search',
                    query: {
                      lat: currentAddress?.lat,
                      lng: currentAddress?.lng,
                    },
                  },
                  undefined,
                  { shallow: true },
                )
              }
            >
              <FaSearch />
              <span>Search for shops & restaurants</span>
            </button>
          </Content>
        </FadeHeader>
        {deferredPrompt && (
          <div className="bg-primary-600 text-white px-[12px] py-[6px] z-[100]">
            <Content>
              <button
                onClick={handleInstall}
                className="flex items-center gap-[6px] font-bold"
              >
                <span>{t('panther:app_download_prompt')}</span>
                <RiDownload2Fill />
              </button>
            </Content>
          </div>
        )}
        <DiscountConstantDisplay />
        <Stack spacing="12px" className="min-h-screen">
          {/* <div className="w-full flex flex-col items-center justify-center">
            <Content className="w-full px-0">
              <Swiper
                // grapCursor={true}
                loop
                autoplay={{
                  delay: 3500,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                }}
                modules={[Autoplay, Pagination]}
                className="mySwiper"
              >
                <SwiperSlide>
                  <ChakraImage
                    src={'/static/images/tmp/추석연휴이벤트.png'}
                    alt="banner event 1"
                    objectFit="contain"
                    w="100%"
                  />
                </SwiperSlide>
              </Swiper>
            </Content>
          </div> */}
          <Content className="w-full">
            <SectionTitle className="text-lg mb-[6px] font-bold">
              Your favourite cuisines
            </SectionTitle>
          </Content>
          <Content className="w-full mb-[12px]">
            <MainContainer />
          </Content>
          {status === 'authenticated' && (
            <Content className="w-full my-[12px]">
              <SectionTitle className="text-lg mb-[18px] font-bold">
                {t(`home:recent_orders`)}
              </SectionTitle>
              <div className="my-[8px] overflow-auto">
                <RecentStore />
              </div>
            </Content>
          )}
          {currentAddress?.lat && currentAddress?.lng && (
            <Content className="w-full my-[12px]">
              <SectionTitle className="text-lg mb-[18px] font-bold">
                All restaurants
              </SectionTitle>
              <div className="my-[8px]">
                <Suspense>
                  <RecommendedStore />
                </Suspense>
              </div>
            </Content>
          )}
        </Stack>
      </ConfigProvider>
    </>
  );
}

export const getServerSideProps = withConfig(async (_, config) => {
  return {
    props: {
      config,
    },
  };
});
